import { TZDateMini } from '@date-fns/tz';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type RefObject,
  FocusEventHandler,
} from 'react';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useDateTimeRangeErrorMessage } from './useDateTimeRangeErrorMessage.js';
import type { TimeValue } from '../../../core/types/time.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import { FormFieldMessagesAction } from '../../forms-core/state/formfield-messages-state-reducer.js';
import type {
  DateTimeError,
  DateTimeInputRef,
  DateTimeRangeError,
  DateTimeRangePickerProps,
  ValidationError,
} from '../types.js';

export const useDateTimeRangePickerValidation = (
  touchedContainerRef: RefObject<HTMLElement | null>,
  validationInputRef: RefObject<DateTimeInputRef | null>,
  valueFrom: TimeValue | null,
  valueTo: TimeValue | null,
  validationProps: Pick<DateTimeRangePickerProps, 'max' | 'min' | 'precision'>,
) => {
  const [errorFrom, setErrorFrom] = useState<DateTimeRangeError>(null);
  const [errorTo, setErrorTo] = useState<DateTimeRangeError>(null);

  const { dispatch: dispatchErrorMessage } = useFormFieldMessagesContext();
  const { getErrorMessage } = useDateTimeRangeErrorMessage(validationProps);

  // use shared now value for all expression parsing
  const sharedNow = useRef<number>(TZDateMini.tz(getTimezone()).getTime());

  const dateTimeRangePickerErrorId = useId();

  // Create a timevalue based on current expression
  const evaluate = useCallback(
    (input: string | null) =>
      parseTimeAsTimeValue(input, sharedNow.current, validationProps.precision),
    [validationProps.precision],
  );

  // Update the timestamp for expression base timevalue instances
  const reevaluate = useCallback(
    (value: TimeValue | null) => {
      if (value?.type === 'expression') {
        return evaluate(value.value);
      }

      return value;
    },
    [evaluate],
  );

  const validate = useCallback(
    (
      from: TimeValue | null,
      to: TimeValue | null,
      showInitialError?: boolean,
    ) => {
      // Push any errors to the messages slot
      function pushError(error: ValidationError) {
        // remove old errors
        dispatchErrorMessage?.({
          type: FormFieldMessagesAction.REMOVEMESSAGE,
          payload: {
            id: dateTimeRangePickerErrorId,
            messageType: 'formcontrol',
          },
        });

        dispatchErrorMessage?.({
          type: FormFieldMessagesAction.ADDMESSAGE,
          payload: {
            id: dateTimeRangePickerErrorId,
            validityStateKey: 'customError',
            variant: 'error',
            message: getErrorMessage(error),
            messageType: 'formcontrol',
            showError: showInitialError,
          },
        });
      }

      // Check if one of the values is affected by given error
      function validateFor(error: DateTimeError) {
        if (errorFrom === error || errorTo === error) {
          pushError({
            error,
            source:
              errorFrom === errorTo
                ? 'both'
                : errorFrom === error
                ? 'from'
                : 'to',
          });
          return false;
        }
        return true;
      }

      // Check if one value is set, the other one has to be set as well. Regardless if required or not.
      function validateForBothValues() {
        if ((from && !to) || (to && !from)) {
          pushError({
            error: 'valueMissing',
            source: from ? 'to' : 'from',
          });
          return false;
        }
        return true;
      }

      const isValid =
        // If one of the field is invalid, this is always the error with the highest priority
        validateFor('invalid') &&
        // If on value is there, the one one has to be there as well
        validateForBothValues() &&
        // Next, prioritize if a value is missing
        validateFor('valueMissing') &&
        // Next, check if both values are within given boundaries
        validateFor('outsideRange');

      if (!isValid) {
        return; //nothing more to do
      }

      // Check if the from && to values are in correct order
      // Reevaluate any expression upfront to have the same 'now' as base to compare both values
      const newFrom = reevaluate(from);
      const newTo = reevaluate(to);

      const timezone = getTimezone();
      if (
        newFrom &&
        newTo &&
        new TZDateMini(newFrom.absoluteDate, timezone).getTime() >=
          new TZDateMini(newTo.absoluteDate, timezone).getTime()
      ) {
        pushError({ error: 'invalidOrder', source: 'both' });
        return;
      }

      // Everything seems to be fine, clear any existing error
      dispatchErrorMessage?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: {
          id: dateTimeRangePickerErrorId,
          messageType: 'formcontrol',
        },
      });
    },
    [
      dateTimeRangePickerErrorId,
      dispatchErrorMessage,
      errorFrom,
      errorTo,
      getErrorMessage,
      reevaluate,
    ],
  );

  // mark the component as touched, if the touchedRef container is losing the focus
  const onBlurTouched: FocusEventHandler = useCallback(
    (e) => {
      if (
        e.relatedTarget !== null && // otherwise on outside clicks, we would see validation errors briefly before closing the overlay
        !touchedContainerRef.current?.contains(e.relatedTarget)
      ) {
        //  always validate and show error on blur if there is any
        validate(valueFrom, valueTo);
        dispatchErrorMessage?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
    },
    // no need to set the touched refs as dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validate, valueFrom, valueTo],
  );

  // error validation effect
  useEffect(() => {
    validate(valueFrom, valueTo);
  }, [validate, valueFrom, valueTo]);

  // push any error to context if value is invalid
  useEffect(() => {
    const onInvalid = () => {
      dispatchErrorMessage?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: true },
      });
    };
    const inputEl = validationInputRef.current?.inputRef;

    inputEl?.addEventListener('invalid', onInvalid);

    return () => {
      inputEl?.removeEventListener('invalid', onInvalid);

      dispatchErrorMessage?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: {
          id: dateTimeRangePickerErrorId,
          messageType: 'formcontrol',
        },
      });
    };
    // no use to provide the input ref in the dep array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    evaluate,
    reevaluate,
    onBlurTouched,
    onError: {
      from: setErrorFrom,
      to: setErrorTo,
    },
    errorFrom,
    errorTo,
    validate,
  };
};
